import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import AssetsService from '../../services/AssetsService';
import CodeService from '../../services/CodeService';
import RentalService from '../../services/RentalService';
import { Station, Lock } from '../../services/models/assets';

import ConsolePanel from './components/ConsolePanel';
import StationArchitectureDiagram from './components/StationArchitectureDiagram';
import BulkActionsPanel from './components/BulkActionsPanel';
import ModalOpenLock from '../../components/Modals/ModalOpenLock';
import ModalGenerateCode from '../../components/Modals/ModalGenerateCode';
import ModalOpenAllLocks from '../../components/Modals/ModalOpenAllLocks';
import ModalGenerateMultipleCodes from '../../components/Modals/ModalGenerateMultipleCodes';
import ModalRevokeAllCodes from '../../components/Modals/ModalRevokeAllCodes';
import useLocationRole from '../../hooks/useLocationRole';

const StationOperations = () => {
  const { siteId, stationId } = useParams<{ siteId: string; stationId: string }>();

  // Access codes are part of the rentals feature. Read per-location, so a
  // teammate with rentals on one site and not another gets the right answer on
  // each; for an owner this is their own feature flag.
  const { rentalsEnabled } = useLocationRole(siteId);
  const numericStationId = Number(stationId);
  const queryClient = useQueryClient();

  // Modal state
  const [openLockLock, setOpenLockLock] = useState<Lock | null>(null);
  const [generateCodeLock, setGenerateCodeLock] = useState<Lock | null>(null);
  const [generateCodeError, setGenerateCodeError] = useState<string | null>(null);
  const [generateCodeResult, setGenerateCodeResult] = useState<{ token: string } | null>(null);
  const [showOpenAllLocks, setShowOpenAllLocks] = useState(false);
  const [showGenerateMultiple, setShowGenerateMultiple] = useState(false);
  const [showRevokeAll, setShowRevokeAll] = useState(false);

  // Station data
  const { data: station, isError } = useQuery({
    queryKey: ['stations', siteId, stationId],
    queryFn: () => AssetsService.getStations(Number(siteId!)),
    select: (data): Station | undefined =>
      data.stations.find((s) => s.id === Number(stationId)),
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });

  const { data: activeCodes } = useQuery({
    queryKey: ['codes', numericStationId, 'active'],
    queryFn: () => CodeService.findAllByStationId(numericStationId, 'active'),
    // Only feeds the revoke-all modal, which is unreachable without rentals.
    enabled: !!stationId && rentalsEnabled,
    select: (data) => data.codes,
  });

  // Get all locks from station ECUs
  const allLocks: Lock[] = station?.ecus
    ?.sort((a, b) => a.address - b.address)
    .flatMap((ecu) => ecu.locks || []) ?? [];

  // Single-lock mutations (architecture diagram actions)
  const openLockMutation = useMutation({
    mutationFn: ({ token, lockId }: { token: string; lockId: number }) =>
      AssetsService.openLock(token, lockId),
    onSuccess: () => {
      toast.success('Action envoyée');
    },
    onError: (error: any) => {
      // A 403 now covers two different things: the station's owner has no plan,
      // or the station is outside what this teammate may reach. Blanket-labelling
      // both "Abonnement actif requis" is what made the teams bug so confusing.
      if (error?.status === 403) {
        const noPlan = typeof error?.message === 'string'
          && error.message.includes('subscription');
        toast.error(noPlan ? 'Abonnement actif requis' : 'Accès refusé à cette station');
      } else {
        toast.error('Erreur lors de l\'ouverture');
      }
    },
    onSettled: () => setOpenLockLock(null),
  });

  const generateCodeMutation = useMutation({
    mutationFn: async (data: {
      lockId: number;
      startDate: string | null;
      duration: number;
      durationUnit: 'hours' | 'days';
      price?: number;
      deposit?: number;
      userId?: number;
      customer?: { firstName?: string; lastName?: string; phone?: string; email?: string };
      accessOnly: boolean;
    }): Promise<{ token: string }> => {
      if (data.accessOnly) {
        const code = await CodeService.create({
          lockId: data.lockId,
          startDate: data.startDate,
          duration: data.duration,
          durationUnit: data.durationUnit,
        });
        return { token: code.token };
      }
      const response = await RentalService.createManual({
        lockIds: [data.lockId],
        startDate: data.startDate,
        duration: data.duration,
        durationUnit: data.durationUnit,
        // price/deposit are entered in euros; the API stores cents.
        price: data.price != null ? Math.round(data.price * 100) : undefined,
        deposit: data.deposit != null ? Math.round(data.deposit * 100) : undefined,
        userId: data.userId,
        customer: data.customer,
      });
      return { token: response.codes[0]?.token ?? '' };
    },
    onSuccess: (result: { token: string }) => {
      setGenerateCodeResult(result);
      queryClient.invalidateQueries({ queryKey: ['codes', numericStationId] });
      queryClient.invalidateQueries({ queryKey: ['rentals', numericStationId] });
    },
    onError: (error: any) => {
      setGenerateCodeError(error?.message || 'Erreur lors de la génération');
    },
  });

  const toggleLockMutation = useMutation({
    mutationFn: ({ lockId, action }: { lockId: number; action: 'activate' | 'deactivate' }) =>
      AssetsService.toggleLockActive(lockId, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stations', siteId, stationId] });
      toast.success('Lock mis à jour');
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour du lock');
    },
  });

  // Bulk mutations (quick actions)
  const openAllLocksMutation = useMutation({
    mutationFn: async () => {
      if (!station) throw new Error('No station');
      const activeLocks = allLocks.filter((l) => l.active);
      await Promise.all(
        activeLocks.map((lock) => AssetsService.openLock(station.token, lock.id)),
      );
    },
    onSuccess: () => {
      toast.success('Actions envoyées');
      setShowOpenAllLocks(false);
    },
    onError: () => {
      toast.error('Erreur lors de l\'ouverture des locks');
    },
  });

  const generateMultipleMutation = useMutation({
    mutationFn: async (data: {
      lockIds: number[];
      startDate?: string | null;
      duration: number;
      durationUnit: 'hours' | 'days';
      price?: number;
      deposit?: number;
      userId?: number;
      customer?: { firstName?: string; lastName?: string; phone?: string; email?: string };
      accessOnly: boolean;
    }) => {
      if (data.accessOnly) {
        return CodeService.generateMultiple(numericStationId, {
          lockIds: data.lockIds,
          startDate: data.startDate,
          duration: data.duration,
          durationUnit: data.durationUnit,
        });
      }
      return RentalService.createManual({
        lockIds: data.lockIds,
        startDate: data.startDate,
        duration: data.duration,
        durationUnit: data.durationUnit,
        // price/deposit entered in euros; the API stores cents.
        price: data.price != null ? Math.round(data.price * 100) : undefined,
        deposit: data.deposit != null ? Math.round(data.deposit * 100) : undefined,
        userId: data.userId,
        customer: data.customer,
      });
    },
    onSuccess: () => {
      toast.success('Créé');
      setShowGenerateMultiple(false);
      queryClient.invalidateQueries({ queryKey: ['codes', numericStationId] });
      queryClient.invalidateQueries({ queryKey: ['rentals', numericStationId] });
    },
    onError: () => {
      toast.error('Erreur lors de la création');
    },
  });

  const revokeAllMutation = useMutation({
    mutationFn: () => CodeService.revokeAllByStationId(numericStationId),
    onSuccess: () => {
      toast.success('Codes révoqués');
      setShowRevokeAll(false);
      queryClient.invalidateQueries({ queryKey: ['codes', numericStationId] });
    },
    onError: () => {
      toast.error('Erreur lors de la révocation');
    },
  });

  // Handlers (architecture diagram)
  const handleOpenLock = (lock: Lock) => {
    setOpenLockLock(lock);
  };

  const confirmOpenLock = () => {
    if (!station || !openLockLock) return;
    openLockMutation.mutate({ token: station.token, lockId: openLockLock.id });
  };

  const handleGenerateCode = (lock: Lock) => {
    setGenerateCodeLock(lock);
    setGenerateCodeError(null);
    setGenerateCodeResult(null);
  };

  const handleToggleLock = (lock: Lock) => {
    toggleLockMutation.mutate({ lockId: lock.id, action: lock.active ? 'deactivate' : 'activate' });
  };

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-error-600 typo-small">Erreur lors du chargement de la station.</span>
      </div>
    );
  }

  if (!station) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-neutral-40 typo-small">Chargement...</span>
      </div>
    );
  }

  const lastSync = station.updatedAt
    ? `Dernière sync: ${formatTimeAgo(new Date(station.updatedAt))}`
    : '';

  return (
    <div className="w-full max-w-[1280px] px-4 flex flex-col gap-6 pb-8">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h1 className="typo-h1 text-neutral">Opérations</h1>
        <p className="typo-small text-neutral-40">
          {station.name || `Station ${station.id}`} · {lastSync}
        </p>
      </div>

      {/* Station architecture — per-lock actions (open, generate access, toggle) */}
      <StationArchitectureDiagram
        station={station}
        codesEnabled={rentalsEnabled}
        onOpenLock={handleOpenLock}
        onGenerateCode={handleGenerateCode}
        onToggleLock={handleToggleLock}
      />

      {/* Bulk Actions */}
      <BulkActionsPanel
        lockCount={allLocks.filter((l) => l.active).length}
        codesEnabled={rentalsEnabled}
        onOpenAllLocks={() => setShowOpenAllLocks(true)}
        onGenerateMultipleCodes={() => setShowGenerateMultiple(true)}
        onRevokeAllCodes={() => setShowRevokeAll(true)}
      />

      {/* Console — last on the page so its own scroll doesn't trap the page scroll */}
      <ConsolePanel stationId={numericStationId} locks={allLocks} />

      {/* Modals */}
      <ModalOpenLock
        visible={openLockLock !== null}
        lock={openLockLock}
        onCancel={() => setOpenLockLock(null)}
        onConfirm={confirmOpenLock}
        isLoading={openLockMutation.isPending}
      />
      <ModalGenerateCode
        visible={generateCodeLock !== null}
        lock={generateCodeLock}
        onCancel={() => { setGenerateCodeLock(null); setGenerateCodeResult(null); }}
        onSubmit={(data) => { setGenerateCodeError(null); generateCodeMutation.mutate(data); }}
        isLoading={generateCodeMutation.isPending}
        error={generateCodeError}
        result={generateCodeResult}
      />
      <ModalOpenAllLocks
        visible={showOpenAllLocks}
        lockCount={allLocks.filter((l) => l.active).length}
        onCancel={() => setShowOpenAllLocks(false)}
        onConfirm={() => openAllLocksMutation.mutate()}
        isLoading={openAllLocksMutation.isPending}
      />
      <ModalGenerateMultipleCodes
        visible={showGenerateMultiple}
        locks={allLocks}
        onCancel={() => setShowGenerateMultiple(false)}
        onSubmit={(data) => generateMultipleMutation.mutate(data)}
        isLoading={generateMultipleMutation.isPending}
        error={generateMultipleMutation.error?.message ?? null}
      />
      <ModalRevokeAllCodes
        visible={showRevokeAll}
        activeCodeCount={activeCodes?.length ?? 0}
        onCancel={() => setShowRevokeAll(false)}
        onConfirm={() => revokeAllMutation.mutate()}
        isLoading={revokeAllMutation.isPending}
      />
    </div>
  );
};

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `il y a ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

export default StationOperations;
